import NetIncomeCalculator from '@fxxl/net_income_calculator';
import { LEASING_CALCULATOR_CONFIG } from '@fxxl/configs/leasing_calculator_configs';
import FxxlError from '@fxxl/error';

type ProviderKey = keyof typeof LEASING_CALCULATOR_CONFIG.provider;
type ServiceFee = {min: number, max: number};

interface LeasingOptions {
    productPrice: number;
    provider?: ProviderKey;
    employerPaysProtectionPackage?: boolean;
    grossIncome?: number;
    federalState?: string;
    taxClass?: number;
    childrenCount?: number;
    churchTax?: boolean;
    healthInsuranceType?: string;
    healthInsuranceAdditionalContribution?: number;
    birthYear?: number;
    childless?: boolean;
}

export default class FxxlLeasingCalculator {
    public calculateLeasingCost(options: LeasingOptions) {
        // Optionen mit Standardwerten zusammenführen
        const merged = this.mergeOptions(options);
        const providerConfig = this.getProviderConfig(merged.provider);
        const config = { ...merged, providerConfig };
        // Altes Nettoeinkommen berechnen
        const oldNetIncomeCalculator = new NetIncomeCalculator(config);
        const oldNetIncomeInfos = oldNetIncomeCalculator.calculateNetIncome();
        const oldNetIncome = oldNetIncomeInfos.netIncome;
        // Leasingpreis berechnen
        const leasingPrice = options.productPrice * (1 + this.dynamicServiceFeeCalculation(options.productPrice, providerConfig.service_fee));
        // Monatliche Leasingrate berechnen
        const montlyLeasingRate = leasingPrice / LEASING_CALCULATOR_CONFIG.leaseTermMonths;
        // Privatnutzungsanteil berechnen
        const privateUseCost = options.productPrice * LEASING_CALCULATOR_CONFIG.privateUseRate;

        // Schutzbriefkosten berechnen
        let protectionPackageCost = 0;
        const potentialProtectionPackageCost = options.productPrice * LEASING_CALCULATOR_CONFIG.protectionPackage;
        if (!merged.employerPaysProtectionPackage) {
            protectionPackageCost = options.productPrice * LEASING_CALCULATOR_CONFIG.protectionPackage;
        }

        // Neue monatliche Leasingrate nach Berücksichtigung von Privatnutzung und Schutzbrief
        const endMonthlyLeasingRate = montlyLeasingRate + protectionPackageCost - privateUseCost;

        // Neues Nettoeinkommen berechnen
        const newGrossIncome = merged.grossIncome - endMonthlyLeasingRate;
        const newNetIncomeCalculator = new NetIncomeCalculator({ ...merged, grossIncome: newGrossIncome });
        const newNetIncomeInfos = newNetIncomeCalculator.calculateNetIncome();
        // Neues Nettoeinkommen nach Privatnutzung
        const newNetIncome = newNetIncomeInfos.netIncome - privateUseCost;
        // Tatsächlich zu zahlender Netto-Betrag
        const netMonthlyCost = Math.round(oldNetIncome - newNetIncome);
        const netCompleteCost = netMonthlyCost * LEASING_CALCULATOR_CONFIG.leaseTermMonths;
        // Ergebnis zurückgeben
        return {
            monthlyLeasingRate: parseFloat(endMonthlyLeasingRate.toFixed(2)),
            netMonthlyCost: netMonthlyCost,
            netCompleteCost: netCompleteCost,
            oldNetIncome: parseFloat(oldNetIncome.toFixed(2)),
            newNetIncome: parseFloat(newNetIncome.toFixed(2)),
            protectionPackageCost: parseFloat((potentialProtectionPackageCost * LEASING_CALCULATOR_CONFIG.leaseTermMonths).toFixed(2)),
            service_fee: parseFloat((leasingPrice - options.productPrice).toFixed(2)),
        };
    }

    private dynamicServiceFeeCalculation(price: number, service_fee: ServiceFee): number {
        const p1 = 3000, v1 = service_fee.max;
        const p2 = 8000, v2 = service_fee.min;

        // Steigung
        const m = (v2 - v1) / (p2 - p1);

        // Linear berechneter Wert
        let value = v1 + m * (price - p1);
        value = Math.min(Math.max(value, v2), v1);
        return value;
    }

    private mergeOptions(options: LeasingOptions): Required<LeasingOptions> {
        return {
            provider: 'jobrad',
            employerPaysProtectionPackage: true,
            grossIncome: 3000,
            federalState: 'HE',
            taxClass: 3,
            childrenCount: 0,
            churchTax: true,
            healthInsuranceType: 'gesetzlich',
            healthInsuranceAdditionalContribution: 2.5,
            birthYear: 1979,
            childless: true,
            ...options,
        };
    }

    private getProviderConfig(provider: ProviderKey) {
        const config = LEASING_CALCULATOR_CONFIG.provider?.[provider];
        if (!config) {
            const available = Object.keys(LEASING_CALCULATOR_CONFIG.provider).join(', ');
            throw new FxxlError(`Unknown provider '${String(provider)}'. Available: ${available}`);
        }
        return config;
    }
}
